import React, { useContext, useMemo } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import ContentContext from "../../../contexts/ContentContext";
import InputDuration from "../../utils/InputDuration";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type {
  DurationType,
  GetMediaResponse,
  MediaChildren,
} from "../../../api/MediaAPI.types";
import type { ContentContextValues } from "../../content/ContentTabs";

interface MediaDurationProps {
  item: GetMediaResponse | MediaChildren;
  onItemUpdate: (item: GetMediaResponse | MediaChildren) => void;
}

interface DurationTypeOption {
  label: string;
  value: string;
  data: DurationType;
}

export default function MediaDuration({
  item,
  onItemUpdate,
}: MediaDurationProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { isDisabled, metadatas } =
    useContext<ContentContextValues>(ContentContext);

  const durationTypeOptions = useMemo<DurationTypeOption[] | undefined>(() => {
    const durationTypes = metadatas?.data.duration_types;

    return durationTypes
      ?.map((durationType) => ({
        label: durationType.duration,
        value: durationType.uuid,
        data: durationType,
      }))
      .sort((a, b) => a.data.rank - b.data.rank);
  }, [metadatas?.data]);

  const onDurationChange = (duration: string): void => {
    const updatedItem = {
      ...item,
      duration: moment(duration, "HH:mm:ss").diff(
        moment().startOf("day"),
        "seconds",
      ),
    };

    onItemUpdate(updatedItem);
  };

  const onDurationType = (option: SingleValue<DurationTypeOption>): void => {
    if (!option) return;

    onItemUpdate({ ...item, duration_type: option.data });
  };

  return (
    <React.Fragment>
      <div>
        <div className="text-foreground py-1.25 text-[15px] font-medium">
          {t("duration")}
        </div>
        <div className="max-w-37.5">
          <InputDuration
            disabled={isDisabled}
            onChange={onDurationChange}
            placeholder={t("durationPlaceholder")}
            value={item.duration}
          />
        </div>
      </div>
      <div>
        <div className="text-foreground py-1.25 text-[15px] font-medium">
          {t("duration_type")}
        </div>
        <div className="max-w-37.5">
          <Select
            closeMenuOnSelect
            className="editor-select"
            classNamePrefix="slct"
            isDisabled={isDisabled}
            name="form-field-name"
            onChange={onDurationType}
            options={durationTypeOptions}
            placeholder={t("select")}
            value={
              item.duration_type
                ? {
                    value: item.duration_type.uuid,
                    label: item.duration_type.duration,
                    data: item.duration_type,
                  }
                : null
            }
          />
        </div>
      </div>
    </React.Fragment>
  );
}
